import { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import { ApiError } from "@/api/http";
import {
  riskLimits,
  type RiskLimit,
  type RiskLimitCreate,
} from "@/api/riskLimits";
import { Modal } from "@/components/Modal";
import { useToast } from "@/components/Toast";
import {
  Badge,
  Button,
  Card,
  CardBody,
  EmptyState,
  FormError,
  Input,
  Label,
  Select,
} from "@/components/ui";

const LIMIT_TYPES = [
  "max_position_size",
  "max_daily_drawdown_pct",
  "max_open_positions",
  "max_order_qty",
  "max_notional_per_order",
  "max_strategy_loss",
];

export function AdminRiskLimitsPage() {
  const list = useQuery({ queryKey: ["risk-limits"], queryFn: () => riskLimits.list() });
  const [open, setOpen] = useState(false);
  const qc = useQueryClient();
  const toast = useToast();

  const toggle = useMutation({
    mutationFn: (r: RiskLimit) =>
      riskLimits.patch(r.id, { enabled: !r.enabled }),
    onSuccess: () => {
      toast.push("Limit updated", "success");
      qc.invalidateQueries({ queryKey: ["risk-limits"] });
    },
    onError: (e) =>
      toast.push(e instanceof ApiError ? e.message.replace(/^\d+:\s*/, "") : String(e), "error"),
  });
  const remove = useMutation({
    mutationFn: (id: string) => riskLimits.remove(id),
    onSuccess: () => {
      toast.push("Limit deleted", "warn");
      qc.invalidateQueries({ queryKey: ["risk-limits"] });
    },
    onError: (e) =>
      toast.push(e instanceof ApiError ? e.message.replace(/^\d+:\s*/, "") : String(e), "error"),
  });

  return (
    <div className="space-y-3">
      <div className="flex items-center justify-between">
        <h1 className="text-base font-semibold text-term-fg">Risk limits</h1>
        <Button onClick={() => setOpen(true)}>New limit</Button>
      </div>

      <Card>
        <CardBody className="p-0">
          {list.isLoading ? (
            <div className="p-3 text-sm text-term-mute">Loading…</div>
          ) : list.data?.length === 0 ? (
            <EmptyState
              title="No risk limits configured"
              description="Configure org-wide gates: max position size, daily drawdown stop-out, max order qty."
              action={<Button onClick={() => setOpen(true)}>Add first limit</Button>}
            />
          ) : (
            <table className="w-full text-xs">
              <thead className="bg-term-elev text-term-mute">
                <tr>
                  <th className="px-3 py-1.5 text-left text-2xs uppercase tracking-wider">Type</th>
                  <th className="px-3 py-1.5 text-right text-2xs uppercase tracking-wider">Value</th>
                  <th className="px-3 py-1.5 text-left text-2xs uppercase tracking-wider">Scope</th>
                  <th className="px-3 py-1.5 text-left text-2xs uppercase tracking-wider">Status</th>
                  <th className="px-3 py-1.5 text-right text-2xs uppercase tracking-wider">Actions</th>
                </tr>
              </thead>
              <tbody>
                {list.data?.map((r) => (
                  <tr key={r.id} className="border-t border-term-border hover:bg-term-elev">
                    <td className="px-3 py-1.5 font-mono text-term-fg">{r.limit_type}</td>
                    <td className="px-3 py-1.5 text-right tnum text-term-fg">{r.limit_value}</td>
                    <td className="px-3 py-1.5 text-2xs text-term-dim">
                      {r.user_id ? `user ${r.user_id.slice(0, 8)}` : "org"}
                      {r.strategy_id ? ` · strat ${r.strategy_id}` : ""}
                    </td>
                    <td className="px-3 py-1.5">
                      <Badge tone={r.enabled ? "good" : "neutral"}>{r.enabled ? "ON" : "OFF"}</Badge>
                    </td>
                    <td className="px-3 py-1.5 text-right">
                      <div className="flex justify-end gap-2">
                        <Button variant="ghost" onClick={() => toggle.mutate(r)} disabled={toggle.isPending}>
                          {r.enabled ? "Disable" : "Enable"}
                        </Button>
                        <Button
                          variant="danger"
                          onClick={() => {
                            if (confirm(`Delete ${r.limit_type}?`)) remove.mutate(r.id);
                          }}
                        >
                          Delete
                        </Button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </CardBody>
      </Card>

      {open && <CreateLimitModal onClose={() => setOpen(false)} />}
    </div>
  );
}


function CreateLimitModal({ onClose }: { onClose: () => void }) {
  const qc = useQueryClient();
  const toast = useToast();
  const [form, setForm] = useState<RiskLimitCreate>({
    limit_type: LIMIT_TYPES[0],
    limit_value: "100",
    enabled: true,
  });
  const [err, setErr] = useState<string | null>(null);

  const create = useMutation({
    mutationFn: () => riskLimits.create(form),
    onSuccess: () => {
      toast.push("Risk limit created", "success");
      qc.invalidateQueries({ queryKey: ["risk-limits"] });
      onClose();
    },
    onError: (e) => setErr(e instanceof ApiError ? e.message.replace(/^\d+:\s*/, "") : String(e)),
  });

  return (
    <Modal open onClose={onClose} title="New risk limit">
      <form
        onSubmit={(e) => {
          e.preventDefault();
          setErr(null);
          create.mutate();
        }}
        className="space-y-3"
      >
        <div>
          <Label>Limit type</Label>
          <Select
            value={form.limit_type}
            onChange={(e) => setForm({ ...form, limit_type: e.target.value })}
          >
            {LIMIT_TYPES.map((t) => (
              <option key={t} value={t}>{t}</option>
            ))}
          </Select>
        </div>
        <div>
          <Label>Limit value</Label>
          <Input
            value={String(form.limit_value)}
            onChange={(e) => setForm({ ...form, limit_value: e.target.value })}
          />
        </div>
        <div>
          <Label>Strategy id (optional)</Label>
          <Input
            value={form.strategy_id ?? ""}
            onChange={(e) => setForm({ ...form, strategy_id: e.target.value || null })}
            placeholder="leave blank for all strategies"
          />
        </div>
        <FormError>{err}</FormError>
        <div className="flex justify-end gap-2 pt-2">
          <Button type="button" variant="ghost" onClick={onClose}>Cancel</Button>
          <Button type="submit" loading={create.isPending}>Create</Button>
        </div>
      </form>
    </Modal>
  );
}
